import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLidarClearance } from "./LidarClearanceContext";
import { createSystem } from "./api";
import t from "./translations";

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function CreateSystemDialog({ open, onClose }: Props) {
  const { regionTags, refetch } = useLidarClearance();
  const [region, setRegion] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRegion(regionTags[0] ?? "");
      setName("");
    }
  }, [open, regionTags]);

  const canSubmit = region.length > 0 && name.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createSystem(region, name.trim());
      await refetch();
      onClose();
    } catch (error) {
      captureException(error, {
        tags: { feature: "lidarClearance.createSystem" },
      });
      toast.error(t.FailedToCreateSystem);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      fullWidth
    >
      <DialogTitle>{t.CreateSystemDialogTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label={t.RegionLabel}
            value={region}
            onChange={(event) => setRegion(event.target.value)}
            disabled={submitting}
          >
            {regionTags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            label={t.NameLabel}
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={submitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t.CancelButton}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t.CreateButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
